import { useEffect, useRef, useState } from 'react';
import { signalRService } from '../services/signalr/SignalRService';
import { HubEvents, SocialEventEnvelope } from '../services/signalr/types';
import './WorldMessageToast.css';

interface WorldToast {
  id: string;
  text: string;
  style?: string;
  fromName: string;
}

interface WorldMessagePayload {
  text?: string;
  style?: string;
}

const DISMISS_MS = 8000;

/**
 * Prominent centered toast for DM/easter-egg "world_message" envelopes.
 * Listens on both delivery paths: the SocialEventReceived hub event and the
 * Unity bridge forward (unity-event with eventName 'world_message'), deduping
 * by timestamp + text so a doubly-delivered message shows once.
 */
export function WorldMessageToast() {
  const [toasts, setToasts] = useState<WorldToast[]>([]);
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const show = (envelope: Partial<SocialEventEnvelope>) => {
      if (envelope.type !== 'world_message' || !envelope.payloadJson) return;
      let payload: WorldMessagePayload;
      try {
        payload = JSON.parse(envelope.payloadJson) as WorldMessagePayload;
      } catch {
        return;
      }
      if (!payload?.text) return;

      const key = `${envelope.ts ?? ''}|${payload.text}`;
      if (seenRef.current.has(key)) return;
      seenRef.current.add(key);

      const toast: WorldToast = {
        id: key,
        text: payload.text,
        style: payload.style,
        fromName: envelope.fromName || 'The World',
      };
      setToasts((prev) => [...prev, toast]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
        seenRef.current.delete(key);
      }, DISMISS_MS);
    };

    const onSocialEvent = (...args: unknown[]) => {
      try {
        const envelope = JSON.parse(args[0] as string) as SocialEventEnvelope;
        show(envelope);
      } catch {
        return;
      }
    };

    const onUnityEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ eventName: string; data: unknown }>).detail;
      if (!detail || detail.eventName !== 'world_message') return;
      try {
        const raw =
          typeof detail.data === 'string' ? JSON.parse(detail.data) : detail.data;
        if (!raw || typeof raw !== 'object') return;
        const obj = raw as Record<string, unknown>;
        if (typeof obj.payloadJson === 'string') {
          show({ ...(obj as unknown as SocialEventEnvelope), type: 'world_message' });
        } else if (typeof obj.text === 'string') {
          show({
            type: 'world_message',
            fromName: typeof obj.fromName === 'string' ? obj.fromName : 'The World',
            ts: typeof obj.ts === 'string' ? obj.ts : '',
            payloadJson: JSON.stringify({ text: obj.text, style: obj.style }),
          });
        }
      } catch {
        return;
      }
    };

    const unsubscribe = signalRService.on(HubEvents.SocialEventReceived, onSocialEvent);
    window.addEventListener('unity-event', onUnityEvent);
    return () => {
      unsubscribe();
      window.removeEventListener('unity-event', onUnityEvent);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="world-message-layer">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`world-message-toast ${toast.style === 'riddle' ? 'world-message-riddle' : ''}`}
          role="status"
        >
          <span className="world-message-icon" aria-hidden="true">
            {toast.style === 'riddle' ? '📜' : '✨'}
          </span>
          <div className="world-message-body">
            <span className="world-message-from">{toast.fromName}</span>
            <p className="world-message-text">{toast.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default WorldMessageToast;
